import { SHStack, SHToggleButtonOptions } from "@components/design-systems";
import {
  AnalysisSummaryDTO,
  PortfolioCompositionSectionDTO,
  PortfolioCompositionSetupDTO,
} from "@models/platform-analysis/entities/steps/summary";
import { PortfolioCompositionViewMode } from "@models/platform-analysis/enums/portfolioCompositionViewMode";
import { useTheme } from "@mui/material";
import { memo, useState } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { PortfolioCompositionTableViewModeOptions } from "../../../../constants";
import { SummaryPortfolioCompositionTableBody } from "../body";
import { SummaryPortfolioCompositionTableHead } from "../head";
import { SummaryPortfolioCompositionTableWrapper } from "../wrapper";

interface SummaryPortfolioCompositionTableProps {
  showControls?: boolean;
  setups?: PortfolioCompositionSetupDTO[];
  sections?: PortfolioCompositionSectionDTO[];
  title?: string | JSX.Element;
  disabled?: boolean;
}

export const SummaryPortfolioCompositionTable = memo(
  ({
    showControls,
    setups,
    sections,
    title,
    disabled,
  }: SummaryPortfolioCompositionTableProps) => {
    const { palette } = useTheme();
    const { control, setValue } = useFormContext<AnalysisSummaryDTO>();

    const excludingSubTotals = useWatch({
      control,
      name: "hidePortfolioCompositionSubtotals",
    });

    const [subTotalViewMode, setSubTotalViewMode] =
      useState<PortfolioCompositionViewMode>(
        excludingSubTotals
          ? PortfolioCompositionViewMode.Excluded
          : PortfolioCompositionViewMode.Included,
      );

    return (
      <SHStack
        sx={{
          overflowX: "auto",
          overflowY: "hidden",
          width: "fit-content",
          gap: 2,
        }}
      >
        <SHStack
          direction={"row"}
          width={"100%"}
          justifyContent={"space-between"}
          justifyItems={"center"}
        >
          {title}

          {showControls && (
            <SHStack
              direction={"row"}
              gap={3}
              sx={{
                button: {
                  fontWeight: 600,
                  fontSize: 14,
                  lineHeight: 1.2,
                  "&:nth-of-type(1)": {
                    "&.Mui-selected": {
                      ":before": {
                        color: "white",
                        content: '"Inc. sub-totals"',
                      },
                    },
                    ":before": {
                      color: palette.text.primary,
                      content: '"Including"',
                    },
                  },
                  "&:nth-of-type(2)": {
                    "&.Mui-selected": {
                      ":before": {
                        color: "white",
                        content: '"Exc. sub-totals"',
                      },
                    },
                    ":before": {
                      color: palette.text.primary,
                      content: '"Excluding"',
                    },
                  },
                },
              }}
            >
              <SHToggleButtonOptions
                disabled={disabled}
                options={PortfolioCompositionTableViewModeOptions}
                value={subTotalViewMode}
                onChange={(_, value) => {
                  if (!value) return;
                  setValue(
                    "hidePortfolioCompositionSubtotals",
                    value === PortfolioCompositionViewMode.Excluded,
                  );
                  setSubTotalViewMode(value);
                }}
              />
            </SHStack>
          )}
        </SHStack>

        <SummaryPortfolioCompositionTableWrapper>
          <SummaryPortfolioCompositionTableHead setups={setups} />
          <SummaryPortfolioCompositionTableBody sections={sections} />
        </SummaryPortfolioCompositionTableWrapper>
      </SHStack>
    );
  },
);
